/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from 'axios';
import { call, put, take, takeEvery, takeLatest } from 'redux-saga/effects';
import { api } from 'services/api';
import { notificationContainer } from 'utils/notificationContainer';
import { PayloadAction } from '@reduxjs/toolkit';
import fileDownload from 'js-file-download';
import {
	TSwissPostIntegrationStatusResponsePayload,
	TSwissPostOrdersRequest,
	TSwissPostOrdersResponsePayload,
	TSwissPostOrderRequest,
	TSwissPostOrderResponsePayload,
	TSwissPostDownloadLabelRequest,
	TSwissPostDownloadLabelResponsePayload,
	TSwissPostDownloadProformaRequest,
	TSwissPostDownloadProformaResponsePayload,
	TSwissPostRequestDeliveryRequest,
	TSwissPostSendLastErrorByEmailRequest,
	TSwissPostUpdateHsCodeResponse,
	THsCodeDescriptionRequest,
	THsCodeDescriptionResponsePayload,
	TSwissPostUpdateOrderFieldValueRequest,
	TUpdateCustomsDescriptionRequest,
	TSwissPostUpdateWeightRequest,
	TSwissPostUploadProformaRequest,
	TSwissPostSignedProformaPreviewRequest,
	TSwissPostSignedProformaPreviewResponsePayload,
	TSwissPostFirstMileLabelRequest,
	TSwissPostFirstMileLabelResponsePayload,
} from './types';
import {
	getIsSwissPostIntegrationEnabledRequest,
	getIsSwissPostIntegrationEnabledSuccess,
	getSwissPostOrdersRequest,
	getSwissPostOrdersSuccess,
	getSwissPostOrderRequest,
	getSwissPostOrderSuccess,
	getSwissPostDownloadLabelRequest,
	getSwissPostDownloadLabelSuccess,
	getSwissPostDownloadProformaRequest,
	getSwissPostDownloadProformaSuccess,
	requestSwissPostDelivery,
	requestSwissPostDeliverySuccess,
	setRequestDeliveryError,
	sendSwissPostLastErrorByEmailRequest,
	sendSwissPostLastErrorByEmailSuccess,
	setSendSwissPostLastErrorByEmailError,
	shipmentsInitState,
	setSwissPostOrderError,
	updateSwissPostHsCodeRequest,
	updateSwissPostHsCodeSuccess,
	getHsCodeDescriptionRequest,
	getHsCodeDescriptionSuccess,
	updateOrderFieldValuesRequest,
	updateOrderFieldValuesSuccess,
	updateCustomsDescriptionsRequest,
	updateCustomsDescriptionsSuccess,
	updateSwissPostWeightRequest,
	updateSwissPostWeightSuccess,
	updateSwissPostWeightFailure,
	getSwissPostOrderRequestWithoutReload,
	uploadSwissPostProformaRequest,
	uploadSwissPostProformaSuccess,
	setSwissPostProformaUploadError,
	getSwissPostUnsignedProformaRequest,
	getSwissPostUnsignedProformaSuccess,
	getSwissPostSignedProformaPreviewRequest,
	getSwissPostSignedProformaPreviewSuccess,
	getFirstMileLabelRequest,
	getFirstMileLabelSuccess,
	setFirstMileLabelErrorDialog,
	setFirstMileLabelError,
} from './reducer';
import { refreshTokenByTimeOutRequest, refreshTokenByTimeOutSuccess } from '../auth/reducer';

// With responseType 'blob' the error body (ResponseMessage) also arrives as a Blob
// and may not be JSON at all (e.g. a gateway error page).
function* getBlobErrorMessage(error: unknown, defaultMessage: string): Generator<any, string, any> {
	if (!axios.isAxiosError(error)) return defaultMessage;
	const data = error.response?.data;
	if (!(data instanceof Blob)) return error.message || defaultMessage;
	try {
		const responseData: string = yield data.text();
		return JSON.parse(responseData)?.message ?? defaultMessage;
	} catch {
		return defaultMessage;
	}
}

function* getIsSwissPostIntegrationEnabledRequestWorker() {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TSwissPostIntegrationStatusResponsePayload = yield call(
			api.shipments.getIsSwissPostIntegrationEnabled,
		);
		yield put(getIsSwissPostIntegrationEnabledSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error))
			notificationContainer(
				error?.response?.data?.message ?? error.message ?? 'File download error',
				'error',
			);
		yield put(shipmentsInitState());
	}
}

function* getSwissPostOrdersRequestWorker(action: PayloadAction<TSwissPostOrdersRequest>) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TSwissPostOrdersResponsePayload = yield call(
			api.shipments.getSwissPostOrders,
			action.payload,
		);
		yield put(getSwissPostOrdersSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error))
			notificationContainer(
				error?.response?.data?.message ?? error.message ?? 'File download error',
				'error',
			);
		yield put(shipmentsInitState());
	}
}

function* getSwissPostOrderRequestWorker(action: PayloadAction<TSwissPostOrderRequest>) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TSwissPostOrderResponsePayload = yield call(
			api.shipments.getSwissPostOrder,
			action.payload,
		);
		yield put(getSwissPostOrderSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			if (error.response?.status === 404) {
				console.log('error.code 404', error?.response?.status);
				yield put(
					setSwissPostOrderError(
						error?.response?.data?.message ?? error.message ?? error ?? 'Order not found',
					),
				);
			} else {
				notificationContainer(
					error?.response?.data?.message ?? error.message ?? error ?? 'File download error',
					'error',
				);

				yield put(getSwissPostOrderSuccess(null));
			}
		}
		// yield put(getSwissPostOrderSuccess(null));
		// yield put(shipmentsInitState());
	}
}

function* getSwissPostDownloadLabelRequestWorker(
	action: PayloadAction<TSwissPostDownloadLabelRequest>,
): Generator<any, void, any> {
	const { payload } = action;
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TSwissPostDownloadLabelResponsePayload = yield call(
			api.shipments.downloadShipmentsLabel,
			payload,
		);
		yield put(getSwissPostDownloadLabelSuccess());
		if (response) {
			yield fileDownload(response, `eas_swiss_post_label__${payload.trackingNumber}.pdf`);
		}
	} catch (error) {
		if (axios.isAxiosError(error)) {
			const responseData = yield error?.response?.data.text().then((data: any) => data);
			notificationContainer(JSON.parse(responseData)?.message ?? 'File download error', 'error');
		}
		yield put(getSwissPostDownloadLabelSuccess());
	}
}

function* getSwissPostDownloadProformaRequestWorker(
	action: PayloadAction<TSwissPostDownloadProformaRequest>,
): Generator<any, void, any> {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TSwissPostDownloadProformaResponsePayload = yield call(
			api.shipments.downloadShipmentsProforma,
			action.payload,
		);
		yield put(getSwissPostDownloadProformaSuccess());
		if (response) {
			yield fileDownload(
				response,
				`eas_swiss_post_proforma__${action.payload.externalOrderId}.pdf`,
			);
		}
	} catch (error) {
		if (axios.isAxiosError(error)) {
			const responseData = yield error?.response?.data.text().then((data: any) => data);
			notificationContainer(JSON.parse(responseData)?.message ?? 'File download error', 'error');
		}
		yield put(getSwissPostDownloadProformaSuccess());
	}
}

function* getSwissPostUnsignedProformaRequestWorker(
	action: PayloadAction<TSwissPostDownloadProformaRequest>,
): Generator<any, void, any> {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TSwissPostDownloadProformaResponsePayload = yield call(
			api.shipments.downloadShipmentsProforma,
			action.payload,
		);
		yield put(getSwissPostUnsignedProformaSuccess(response));
	} catch (error) {
		const message: string = yield call(getBlobErrorMessage, error, 'File download error');
		notificationContainer(message, 'error');
		yield put(getSwissPostUnsignedProformaSuccess(null));
	}
}

function* getSwissPostSignedProformaPreviewRequestWorker(
	action: PayloadAction<TSwissPostSignedProformaPreviewRequest>,
): Generator<any, void, any> {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TSwissPostSignedProformaPreviewResponsePayload = yield call(
			api.shipments.getSwissPostSignedProformaPreview,
			action.payload,
		);
		yield put(getSwissPostSignedProformaPreviewSuccess(response));
	} catch (error) {
		const message: string = yield call(getBlobErrorMessage, error, 'Pro-forma preview error');
		notificationContainer(message, 'error');
		yield put(getSwissPostSignedProformaPreviewSuccess(null));
	}
}

export function* requestSwissPostDeliveryWorker(
	action: PayloadAction<TSwissPostRequestDeliveryRequest>,
) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		yield call(api.shipments.requestSwissPostDelivery, action.payload);
		yield put(requestSwissPostDeliverySuccess());
		notificationContainer('Delivery request has been sent', 'success');
		setTimeout(() => {
			window.location.reload();
		}, 1000);
	} catch (error) {
		if (axios.isAxiosError(error)) {
			// notificationContainer(
			// 	error?.response?.data?.message ?? error.message ?? 'File download error',
			// 	'error',
			// );
			yield put(
				setRequestDeliveryError(
					error?.response?.data?.message ?? error.message ?? 'File download error',
				),
			);
		} else {
			yield put(requestSwissPostDeliverySuccess());
		}
	}
}

export function* sendSwissPostLastErrorByEmailWorker(
	action: PayloadAction<TSwissPostSendLastErrorByEmailRequest>,
) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		yield call(api.shipments.sendSwissPostLastErrorByEmail, action.payload);
		yield put(sendSwissPostLastErrorByEmailSuccess());
		notificationContainer('Email has been sent', 'success');
	} catch (error) {
		if (axios.isAxiosError(error)) {
			yield put(
				setSendSwissPostLastErrorByEmailError(
					error?.response?.data?.message ?? error.message ?? 'Error',
				),
			);
		} else {
			yield put(sendSwissPostLastErrorByEmailSuccess());
		}
	}
}

export function* updateSwissPostHsCodeWorker(
	action: PayloadAction<TSwissPostUpdateHsCodeResponse>,
) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		yield call(api.shipments.updateSwissPostHsCode, action.payload);
		yield put(updateSwissPostHsCodeSuccess());
		notificationContainer(`HS code has been ${action.payload.action}`, 'success');
		yield put(getSwissPostOrderRequestWithoutReload({ orderId: action.payload.orderId }));
	} catch (error) {
		if (axios.isAxiosError(error))
			notificationContainer(
				error?.response?.data?.message ?? error.message ?? 'File download error',
				'error',
			);
		yield put(updateSwissPostHsCodeSuccess());
	}
}

function* getHsCodeDescriptionRequestWorker(action: PayloadAction<THsCodeDescriptionRequest>) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: THsCodeDescriptionResponsePayload = yield call(
			api.shipments.getHsCodeDescription,
			action.payload,
		);
		yield put(getHsCodeDescriptionSuccess(response));
	} catch (error) {
		// An unknown code is a normal outcome of typing, so it must not leave the slice loading.
		if (axios.isAxiosError(error) && error.response?.status !== 404)
			notificationContainer(
				error?.response?.data?.message ?? error.message ?? 'File download error',
				'error',
			);
		yield put(getHsCodeDescriptionSuccess(null));
	}
}

export function* updateOrderFieldValuesRequestWorker(
	action: PayloadAction<TSwissPostUpdateOrderFieldValueRequest>,
) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		yield call(api.shipments.updateOrderFieldValues, action.payload);
		yield put(updateOrderFieldValuesSuccess());
		notificationContainer('Order fields have been updated', 'success');
		yield put(getSwissPostOrderRequestWithoutReload({ orderId: action.payload.orderId }));
	} catch (error) {
		if (axios.isAxiosError(error))
			notificationContainer(
				error?.response?.data?.message ?? error.message ?? 'File download error',
				'error',
			);
		yield put(updateOrderFieldValuesSuccess());
	}
}

export function* updateCustomsDescriptionsRequestWorker(
	action: PayloadAction<TUpdateCustomsDescriptionRequest>,
) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		yield call(api.shipments.updateCustomsDescriptions, action.payload);
		yield put(updateCustomsDescriptionsSuccess());
		notificationContainer('Customs descriptions have been updated', 'success');
		yield put(getSwissPostOrderRequestWithoutReload({ orderId: action.payload.orderId }));
	} catch (error) {
		if (axios.isAxiosError(error))
			notificationContainer(
				error?.response?.data?.message ?? error.message ?? 'File download error',
				'error',
			);
		yield put(updateCustomsDescriptionsSuccess());
	}
}

export function* updateSwissPostWeightWorker(action: PayloadAction<TSwissPostUpdateWeightRequest>) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		yield call(api.shipments.updateSwissPostWeight, action.payload);
		yield put(updateSwissPostWeightSuccess());
		notificationContainer('Weight has been updated', 'success');
		yield put(getSwissPostOrderRequestWithoutReload({ orderId: action.payload.orderId }));
	} catch (error) {
		if (axios.isAxiosError(error))
			notificationContainer(
				error?.response?.data?.message ?? error.message ?? 'Request error',
				'error',
			);
		yield put(updateSwissPostWeightFailure(action.payload.productId));
	}
}

export function* uploadSwissPostProformaRequestWorker(
	action: PayloadAction<TSwissPostUploadProformaRequest>,
) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		yield call(api.shipments.uploadSwissPostProforma, action.payload);
		yield put(uploadSwissPostProformaSuccess());
		notificationContainer('Proforma has been uploaded', 'success');
		yield put(getSwissPostOrderRequestWithoutReload({ orderId: action.payload.orderId }));
	} catch (error) {
		const message = axios.isAxiosError(error)
			? (error?.response?.data?.message ?? error.message ?? 'File upload error')
			: 'File upload error';
		notificationContainer(message, 'error');
		yield put(setSwissPostProformaUploadError(message));
	}
}

export function* getFirstMileLabelRequestWorker(
	action: PayloadAction<TSwissPostFirstMileLabelRequest>,
): Generator<any, void, any> {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TSwissPostFirstMileLabelResponsePayload = yield call(
			api.shipments.getFirstMileLabel,
			action.payload,
		);
		yield put(getFirstMileLabelSuccess());
		if (response) {
			yield fileDownload(
				response,
				`eas_swiss_post_first_mile_label__${action.payload.orderId}.pdf`,
			);
		}
	} catch (error) {
		if (axios.isAxiosError(error)) {
			const responseData = yield error?.response?.data.text().then((data: any) => data);
			const errorMessage = JSON.parse(responseData)?.message;
			if (errorMessage.includes('Franking license not found')) {
				yield put(setFirstMileLabelErrorDialog(errorMessage));
			} else {
				yield put(setFirstMileLabelError(errorMessage ?? 'File download error'));
				console.log('errorMessage', errorMessage);
			}
		}
		// yield put(getFirstMileLabelSuccess());
	}
}
export function* shipmentsSaga() {
	yield takeEvery(
		getIsSwissPostIntegrationEnabledRequest.type,
		getIsSwissPostIntegrationEnabledRequestWorker,
	);
	yield takeEvery(getSwissPostOrdersRequest.type, getSwissPostOrdersRequestWorker);
	yield takeEvery(getSwissPostOrderRequest.type, getSwissPostOrderRequestWorker);
	yield takeEvery(getSwissPostOrderRequestWithoutReload.type, getSwissPostOrderRequestWorker);

	yield takeEvery(getSwissPostDownloadLabelRequest.type, getSwissPostDownloadLabelRequestWorker);
	yield takeEvery(
		getSwissPostDownloadProformaRequest.type,
		getSwissPostDownloadProformaRequestWorker,
	);
	yield takeEvery(requestSwissPostDelivery.type, requestSwissPostDeliveryWorker);
	yield takeEvery(sendSwissPostLastErrorByEmailRequest.type, sendSwissPostLastErrorByEmailWorker);
	yield takeEvery(updateSwissPostHsCodeRequest.type, updateSwissPostHsCodeWorker);
	yield takeEvery(getHsCodeDescriptionRequest.type, getHsCodeDescriptionRequestWorker);
	yield takeEvery(updateOrderFieldValuesRequest.type, updateOrderFieldValuesRequestWorker);
	yield takeEvery(updateCustomsDescriptionsRequest.type, updateCustomsDescriptionsRequestWorker);
	yield takeEvery(updateSwissPostWeightRequest.type, updateSwissPostWeightWorker);
	yield takeEvery(uploadSwissPostProformaRequest.type, uploadSwissPostProformaRequestWorker);
	// takeLatest: only the most recent document may land in the signing dialog
	yield takeLatest(
		getSwissPostUnsignedProformaRequest.type,
		getSwissPostUnsignedProformaRequestWorker,
	);
	yield takeLatest(
		getSwissPostSignedProformaPreviewRequest.type,
		getSwissPostSignedProformaPreviewRequestWorker,
	);
	yield takeEvery(getFirstMileLabelRequest.type, getFirstMileLabelRequestWorker);
}
